import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { PortalEnvironment } from './environments';

/**
 * The parts every shell shares.
 *
 * The redesign draws two different chromes — the educator portal and the
 * console the family and operations use — and they are genuinely different
 * headers rather than one header with different data. What is identical is
 * underneath: how a nav item decides it is current, what the account menu
 * contains, and how sign-out behaves. That lives here, so fixing it fixes both.
 */

/**
 * Items that are current only on their own path. The child space's home is
 * `/student`, and every other learner screen sits under it: matching its
 * children would light "Mis clases" on every page.
 */
const EXACT_ONLY = new Set(['/help', '/student']);

/** True when `to` is the route being shown, including its child routes. */
export const isCurrent = (pathname: string, to: string): boolean =>
  pathname === to || (!EXACT_ONLY.has(to) && pathname.startsWith(`${to}/`));

interface NavListProps {
  environment: PortalEnvironment;
  onNavigate?: () => void;
}

/**
 * The rail's items.
 *
 * The icon arrives from the environment already wrapped, because the two
 * chromes wrap it differently — the console puts it in a tinted `.ic` circle,
 * the portal renders the svg bare — and that is presentation the config owns.
 */
export const PortalNavList: React.FC<NavListProps> = ({ environment, onNavigate }) => {
  const { pathname } = useLocation();

  return (
    <>
      {environment.nav.map((item) => {
        const current = isCurrent(pathname, item.to);
        return (
          <React.Fragment key={item.key}>
            {item.group ? <div className="slb">{item.group}</div> : null}
            <Link
              to={item.to}
              data-label={item.label}
              className={`${environment.navItemClass ?? 'nav'}${current ? ' on' : ''}`}
              aria-current={current ? 'page' : undefined}
              onClick={onNavigate}
            >
              {item.icon}
              {environment.navLabelClass ? (
                <span className={environment.navLabelClass}>{item.label}</span>
              ) : (
                item.label
              )}
              {item.badge ? <span className="n">{item.badge}</span> : null}
            </Link>
          </React.Fragment>
        );
      })}
    </>
  );
};

interface AccountLinksProps {
  environment: PortalEnvironment;
  onSignOut: () => void;
  /** The class each row carries — `.uddi` in the portal, `.mi` in the console. */
  itemClass: string;
}

/**
 * The account menu's rows.
 *
 * The links differ per environment — a teacher has payout settings, a parent
 * has payment methods — and offering either to the wrong role is a dead link
 * in a walkthrough. Sign-out is the one row every environment has, and it is
 * appended here rather than listed in each config so it cannot be forgotten.
 */
export const PortalAccountLinks: React.FC<AccountLinksProps> = ({
  environment,
  onSignOut,
  itemClass,
}) => (
  <>
    {environment.accountLinks.map((link) => (
      <Link key={link.to} className={itemClass} to={link.to}>
        {link.label}
      </Link>
    ))}
    <div className="udddv" />
    <button
      className={itemClass}
      style={{ color: '#E85D5D', width: '100%', border: 'none', background: 'none' }}
      onClick={onSignOut}
    >
      Cerrar sesión
    </button>
  </>
);
